import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { HardDrive, Cpu, Database, Network, Shield, Calendar, Clock, Key, Copy, Eye, EyeOff, CheckCircle2, XCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';

const OverviewTab = ({ agent, metrics }) => {
  const latestMetric = metrics && metrics.length > 0 ? metrics[0] : null;
  const systemDetails = agent.system_details || {};
  const scores = agent.scores || {};
  const securityBreakdown = scores.security?.breakdown || {};
  const performanceBreakdown = scores.performance?.breakdown || {};

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  return (
    <div className="space-y-6">
      {/* Score Breakdown Cards */}
      {(agent.security_score !== null || agent.performance_score !== null) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Security Score Breakdown */}
          {agent.security_score !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Security Score: {agent.security_score?.toFixed(0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Firewall Enabled</span>
                    {securityBreakdown.firewall ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Antivirus Active</span>
                    {securityBreakdown.antivirus ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Secure Boot</span>
                    {securityBreakdown.secure_boot ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">BitLocker Encryption</span>
                    {securityBreakdown.bitlocker ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Admin Accounts (&lt;3)</span>
                    {securityBreakdown.admin_accounts ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Recent Reboot (&lt;7 days)</span>
                    {securityBreakdown.recent_reboot ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Score Breakdown */}
          {agent.performance_score !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  Performance Score: {agent.performance_score?.toFixed(0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">SSD Storage</span>
                    {performanceBreakdown.disk_type ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Disk Space (&gt;10% free)</span>
                    {performanceBreakdown.disk_space ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Disk Health (Healthy)</span>
                    {performanceBreakdown.disk_health ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Modern CPU (Gen 8+)</span>
                    {performanceBreakdown.cpu_generation ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Memory Adequate (&gt;8GB)</span>
                    {performanceBreakdown.memory_total ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Memory Available (&gt;20%)</span>
                    {performanceBreakdown.memory_available ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Device Age (&lt;5 years)</span>
                    {performanceBreakdown.device_age ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* System Metrics */}
      {latestMetric && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
              <Cpu className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{latestMetric.cpu_percent?.toFixed(1)}%</div>
              <Progress value={latestMetric.cpu_percent} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
              <Database className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{latestMetric.memory_percent?.toFixed(1)}%</div>
              <Progress value={latestMetric.memory_percent} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Disk Usage</CardTitle>
              <HardDrive className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{latestMetric.disk_percent?.toFixed(1)}%</div>
              <Progress value={latestMetric.disk_percent} className="mt-2" />
            </CardContent>
          </Card>
        </div>
      )}

      {/* System Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Operating System</span>
              <span className="text-sm font-medium">{systemDetails.os_name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">OS Version</span>
              <span className="text-sm font-medium">{systemDetails.os_version || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Manufacturer</span>
              <span className="text-sm font-medium">{systemDetails.manufacturer || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Model</span>
              <span className="text-sm font-medium">{systemDetails.model || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Serial Number</span>
              <span className="text-sm font-medium">{systemDetails.serial_number || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Current User</span>
              <span className="text-sm font-medium">
                {systemDetails.current_user ? 
                  `${systemDetails.current_user.domain}\\${systemDetails.current_user.username}` : 
                  'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Uptime</span>
              <span className="text-sm font-medium">{formatUptime(systemDetails.uptime_seconds)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hardware</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">CPU Cores</span>
              <span className="text-sm font-medium">{systemDetails.cpu_cores || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Logical Processors</span>
              <span className="text-sm font-medium">{systemDetails.cpu_logical || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Memory</span>
              <span className="text-sm font-medium">
                {systemDetails.memory_available_gb ? `${systemDetails.memory_available_gb.toFixed(1)} GB` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Private IP</span>
              <span className="text-sm font-medium">{systemDetails.private_ip || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Public IP</span>
              <span className="text-sm font-medium">{systemDetails.public_ip || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">MAC Address</span>
              <span className="text-sm font-medium">{systemDetails.mac_address || 'N/A'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-600">Firewall</span>
              <div className="mt-1">
                <Badge variant={systemDetails.firewall_enabled ? 'success' : 'destructive'}>
                  {systemDetails.firewall_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-600">Antivirus</span>
              <div className="mt-1">
                <span className="text-sm font-medium">{systemDetails.antivirus_product || 'N/A'}</span>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-600">AV Status</span>
              <div className="mt-1">
                <Badge variant={systemDetails.antivirus_enabled ? 'success' : 'destructive'}>
                  {systemDetails.antivirus_status || 'Unknown'}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BitLocker Recovery Keys */}
      {systemDetails.bitlocker_keys && systemDetails.bitlocker_keys.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5 text-yellow-600" />
              <span>BitLocker Recovery Keys</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {systemDetails.bitlocker_keys.map((key, index) => (
                <BitLockerKeyRow key={index} keyData={key} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const BitLockerKeyRow = ({ keyData }) => {
  const [visible, setVisible] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(keyData.recovery_key);
    toast.success('Recovery key copied to clipboard');
  };

  const maskKey = (key) => {
    if (!key) return 'N/A';
    return key.replace(/\d/g, '•');
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold text-slate-800">Drive: {keyData.drive}</h4>
          <p className="text-xs text-slate-500 font-mono mt-1">ID: {keyData.key_id?.substring(0, 20)}...</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setVisible(!visible)}
            className="border-yellow-300"
          >
            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={copyToClipboard}
            className="border-yellow-300"
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="bg-white border border-yellow-300 rounded p-3 font-mono text-sm">
        {visible ? keyData.recovery_key : maskKey(keyData.recovery_key)}
      </div>
      {visible && (
        <p className="text-xs text-yellow-700 mt-2">
          ⚠️ Keep this key secure! It can decrypt the drive.
        </p>
      )}
    </div>
  );
};

export default OverviewTab;